import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  LEAD_CHANNELS, LEAD_CHANNEL_LABELS,
  LEAD_MEDIUMS, LEAD_MEDIUM_LABELS,
  LEAD_SOURCES, LEAD_SOURCE_LABELS,
  FormFieldType, CONFORMITY_VALUES,
} from '@troia-v3/types';
import type { AlertLevel, FormField } from '@troia-v3/types';
import { FilterChip } from '@/shared/components/ui/FilterChip';
import { FiltersButton } from '@/shared/components/ui';
import type { LeadsAdvancedFilters } from '../types';
import { useLeadsCampaignsDropdown, useLeadsAdsetsDropdown, useLeadsAdsDropdown, useLeadsOriginsDropdown, useLeadsTagsDropdown, useLeadsTypesDropdown, useFunnelStepsByFunnels } from '../hooks/useLeadsDropdowns';
import { useFunnelsDropdown } from '@/modules/funnels';
import { useFormFields } from '@/shared/hooks/useFormFields';
import { useFormsDropdown } from '@/shared/hooks/useFormsDropdown';
import { pruneStepsOutsideFunnels } from '../utils/funnelStepFilter';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
// ═══════════════════════════════════════════════════════
// FILTER OPTIONS
// ═══════════════════════════════════════════════════════

interface FilterOption {
  value: string;
  label: string;
}

const PRIORITY_OPTIONS: FilterOption[] = [
  { value: 'urgent', label: 'Urgente' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Média' },
  { value: 'low', label: 'Baixa' },
];

const BUSINESS_STATUS_OPTIONS: FilterOption[] = [
  { value: 'pending', label: 'Em andamento' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
];

const TEMPERATURE_OPTIONS: FilterOption[] = [
  { value: 'hot', label: 'Quente' },
  { value: 'warm', label: 'Morna' },
  { value: 'cold', label: 'Fria' },
];

const QUALIFY_STATUS_OPTIONS: FilterOption[] = [
  { value: 'pending', label: 'Pendente' },
  { value: 'qualified', label: 'Qualificado' },
  { value: 'disqualified', label: 'Desqualificado' },
];

// SOURCE_OPTIONS, MEDIUM_OPTIONS, CHANNEL_OPTIONS derivados dos enums canônicos
// em `@troia-v3/types/leads`. Adicionar nova source/medium/channel: editar
// `LEAD_SOURCES`/`LEAD_MEDIUMS`/`LEAD_CHANNELS` lá (single source of truth).
const SOURCE_OPTIONS: FilterOption[] = LEAD_SOURCES.map((s) => ({ value: s, label: LEAD_SOURCE_LABELS[s] }));
const MEDIUM_OPTIONS: FilterOption[] = LEAD_MEDIUMS.map((m) => ({ value: m, label: LEAD_MEDIUM_LABELS[m] }));
const CHANNEL_OPTIONS: FilterOption[] = LEAD_CHANNELS.map((c) => ({ value: c, label: LEAD_CHANNEL_LABELS[c] }));

// ═══════════════════════════════════════════════════════
// FILTER DEFINITIONS & CATEGORIES
// ═══════════════════════════════════════════════════════

interface FilterDefinition {
  key: string;
  label: string;
  type: 'multiselect' | 'text' | 'daterange';
  options?: FilterOption[];
  placeholder?: string;
  /** daterange: chaves reais do estado que o filtro expande (De / Até) */
  fromKey?: 'dateFrom' | 'wonDateFrom' | 'lostDateFrom';
  toKey?: 'dateTo' | 'wonDateTo' | 'lostDateTo';
  /**
   * Texto exibido no lugar de "Nenhuma opção encontrada" quando a lista está
   * vazia por DEPENDÊNCIA, não por falta de dado — caso de "Etapa" sem funil
   * escolhido, onde o usuário precisa saber o que fazer, não que não há nada.
   */
  emptyHint?: string;
}

/** Par De/Até de uma definição daterange (chave sintética → chaves reais). */
function dateRangeKeys(def: FilterDefinition | null | undefined): { fromKey: NonNullable<FilterDefinition['fromKey']>; toKey: NonNullable<FilterDefinition['toKey']> } | null {
  if (def?.type === 'daterange' && def.fromKey && def.toKey) {
    return { fromKey: def.fromKey, toKey: def.toKey };
  }
  return null;
}

// ═══════════════════════════════════════════════════════
// SEÇÃO "FORMULÁRIO" — chaves sintéticas `form:{fieldId}` ↔ filters.formAnswers[]
// ═══════════════════════════════════════════════════════

const FORM_KEY_PREFIX = 'form:';
const formKey = (fieldId: string): string => `${FORM_KEY_PREFIX}${fieldId}`;
const isFormKey = (key: string): boolean => key.startsWith(FORM_KEY_PREFIX);
const fieldIdFromKey = (key: string): string => key.slice(FORM_KEY_PREFIX.length);

const CONFORMITY_LABELS: Record<string, string> = { conforme: 'Conforme', nao_conforme: 'Não conforme', na: 'N/A' };

/** Opções de um campo de escolha — rating/linear_scale/conformity têm domínio fixo. */
function optionsForField(field: FormField): FilterOption[] {
  if (field.type === FormFieldType.RATING) return [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} ★` }));
  if (field.type === FormFieldType.LINEAR_SCALE) return Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  if (field.type === FormFieldType.CONFORMITY) return CONFORMITY_VALUES.map((v) => ({ value: v, label: CONFORMITY_LABELS[v] ?? v }));
  return (field.options ?? []).map((o) => ({ value: o.value, label: o.label }));
}

/** Definições dinâmicas da seção "Formulário" — uma por pergunta de escolha do modelo. */
function buildFormDefinitions(fields: FormField[] | undefined): Record<string, FilterDefinition> {
  const defs: Record<string, FilterDefinition> = {};
  for (const field of fields ?? []) {
    const options = optionsForField(field);
    if (options.length === 0) continue;
    defs[formKey(field.id)] = { key: formKey(field.id), label: field.label, type: 'multiselect', options, placeholder: 'Buscar resposta...' };
  }
  return defs;
}

/** "Formulário respondido" escolhe UM formulário — as perguntas mostradas são as dele. */
const FORM_ID_KEY = 'formAnswersFormId';

const getFormValues = (filters: LeadsAdvancedFilters, fieldId: string): string[] =>
  filters.formAnswers?.find((entry) => entry.fieldId === fieldId)?.values ?? [];

/** Devolve um novo `filters` com a pergunta definida/removida; apaga a chave quando não sobra nenhuma. */
function setFormValues(filters: LeadsAdvancedFilters, fieldId: string, values: string[]): LeadsAdvancedFilters {
  const rest = (filters.formAnswers ?? []).filter((entry) => entry.fieldId !== fieldId);
  const next = values.length > 0 ? [...rest, { fieldId, values }] : rest;
  const { formAnswers: _omit, ...withoutForm } = filters;
  return next.length > 0 ? { ...withoutForm, formAnswers: next } : withoutForm;
}

const FILTER_DEFINITIONS: Record<string, FilterDefinition> = {
  priority: {
    key: 'priority',
    label: 'Prioridade',
    type: 'multiselect',
    options: PRIORITY_OPTIONS,
    placeholder: 'Buscar prioridade...',
  },
  businessStatus: {
    key: 'businessStatus',
    label: 'Status de Negócio',
    type: 'multiselect',
    options: BUSINESS_STATUS_OPTIONS,
    placeholder: 'Buscar status...',
  },
  temperature: {
    key: 'temperature',
    label: 'Temperatura',
    type: 'multiselect',
    options: TEMPERATURE_OPTIONS,
    placeholder: 'Buscar temperatura...',
  },
  qualifyStatus: {
    key: 'qualifyStatus',
    label: 'Qualificação',
    type: 'multiselect',
    options: QUALIFY_STATUS_OPTIONS,
    placeholder: 'Buscar qualificação...',
  },
  source: {
    key: 'source',
    label: 'Fonte',
    type: 'multiselect',
    options: SOURCE_OPTIONS,
    placeholder: 'Buscar fonte...',
  },
  medium: {
    key: 'medium',
    label: 'Meio',
    type: 'multiselect',
    options: MEDIUM_OPTIONS,
    placeholder: 'Buscar meio...',
  },
  channel: {
    key: 'channel',
    label: 'Canal',
    type: 'multiselect',
    options: CHANNEL_OPTIONS,
    placeholder: 'Buscar canal...',
  },
  origin: {
    key: 'origin',
    label: 'Origem',
    type: 'text',
    placeholder: 'Ex: Google Ads, Facebook Ads...',
  },
  type: {
    key: 'type',
    label: 'Tipo',
    type: 'multiselect',
    options: [],
    placeholder: 'Buscar tipo...',
  },
  segment: {
    key: 'segment',
    label: 'Segmento',
    type: 'text',
    placeholder: 'Ex: Tecnologia, Varejo...',
  },
  // Funil/Etapa: opções vêm da API (ver `allFilterDefinitions`). As entradas
  // aqui existem para o filtro ter label/tipo mesmo antes de a lista carregar.
  funnelId: {
    key: 'funnelId',
    label: 'Funil',
    type: 'multiselect',
    options: [],
    placeholder: 'Buscar funil...',
  },
  stepId: {
    key: 'stepId',
    label: 'Etapa',
    type: 'multiselect',
    options: [],
    placeholder: 'Buscar etapa...',
    emptyHint: 'Escolha um funil primeiro — as etapas mudam de funil para funil.',
  },
  // Seção "Período": três ranges de data. A chave `dateRange` (createdAt) é
  // sintética e histórica — mantida para não invalidar filtros persistidos.
  dateRange: {
    key: 'dateRange',
    label: 'Data de criação',
    type: 'daterange',
    fromKey: 'dateFrom',
    toKey: 'dateTo',
  },
  wonDateRange: {
    key: 'wonDateRange',
    label: 'Data do ganho',
    type: 'daterange',
    fromKey: 'wonDateFrom',
    toKey: 'wonDateTo',
  },
  lostDateRange: {
    key: 'lostDateRange',
    label: 'Data da perda',
    type: 'daterange',
    fromKey: 'lostDateFrom',
    toKey: 'lostDateTo',
  },
};

interface FilterCategory {
  id: string;
  label: string;
  icon: IconName;
  filters: string[];
}

const FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'funnel',
    label: 'Funil',
    icon: 'layers',
    filters: ['funnelId', 'stepId'],
  },
  {
    id: 'status',
    label: 'Status',
    icon: 'bar-chart',
    filters: ['priority', 'businessStatus', 'temperature', 'qualifyStatus'],
  },
  {
    id: 'origin',
    label: 'Origem',
    icon: 'globe',
    filters: ['source', 'medium', 'channel', 'origin', 'type', 'segment', 'campaignName', 'adsetName', 'adName'],
  },
  {
    id: 'tags',
    label: 'Tags',
    icon: 'tag',
    filters: ['tags'],
  },
  {
    id: 'period',
    label: 'Período',
    icon: 'calendar',
    filters: ['dateRange', 'wonDateRange', 'lostDateRange'],
  },
];

// Default filters (backend standard)
const DEFAULT_FILTERS: LeadsAdvancedFilters = {
  businessStatus: ['pending'],
};

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
}

function formatDate(dateStr: string): string {
  try {
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) return `${day}/${month}`;
    return dateStr;
  } catch {
    return dateStr;
  }
}

function getActiveFilters(filters: LeadsAdvancedFilters, definitions?: Record<string, FilterDefinition>): ActiveFilter[] {
  const entries: ActiveFilter[] = [];
  const defs = definitions || FILTER_DEFINITIONS;

  const regularKeys = [
    'priority', 'businessStatus', 'temperature',
    'qualifyStatus', 'funnelId', 'stepId', 'source', 'medium', 'channel', 'origin', 'type', 'segment',
    'campaignName', 'adsetName', 'adName', 'tags', 'formAnswersFormId'
  ] as const;

  for (const key of regularKeys) {
    const value = filters[key as keyof LeadsAdvancedFilters];
    if (value === undefined || value === null || value === '') continue;

    const def = defs[key];
    if (!def) continue;

    let displayValue = '';
    if (def.type === 'multiselect' && def.options) {
      const values = Array.isArray(value) ? value : [value];
      const labels = values
        .map(v => def.options!.find(o => o.value === v)?.label)
        .filter(Boolean) as string[];
      if (labels.length === 0) continue;
      if (labels.length <= 2) displayValue = labels.join(', ');
      else displayValue = `${labels[0]}, +${labels.length - 1}`;
    } else if (def.type === 'text') {
      displayValue = String(value);
    }

    entries.push({ key, label: def.label, displayValue });
  }

  // Seção "Formulário" — um chip por pergunta ativa ("Pergunta: valor")
  for (const entry of filters.formAnswers ?? []) {
    const def = definitions?.[formKey(entry.fieldId)];
    if (!def?.options) continue; // pergunta apagada do modelo → chip some (spec §6)
    const labels = entry.values.map((v) => def.options!.find((o) => o.value === v)?.label ?? v);
    if (labels.length === 0) continue;
    const displayValue = labels.length <= 2 ? labels.join(', ') : `${labels[0]}, +${labels.length - 1}`;
    entries.push({ key: def.key, label: def.label, displayValue });
  }

  // Date ranges (composite chips) — um chip por range ativo, label da definição
  for (const def of Object.values(defs)) {
    const range = dateRangeKeys(def);
    if (!range) continue;
    const fromVal = filters[range.fromKey];
    const toVal = filters[range.toKey];
    if (!fromVal && !toVal) continue;

    const fromStr = fromVal ? formatDate(fromVal) : '';
    const toStr = toVal ? formatDate(toVal) : '';
    let displayValue = '';
    if (fromStr && toStr) displayValue = `${fromStr} - ${toStr}`;
    else if (fromStr) displayValue = `a partir de ${fromStr}`;
    else if (toStr) displayValue = `até ${toStr}`;

    entries.push({ key: def.key, label: def.label, displayValue });
  }

  return entries;
}

function isFiltersEqualToDefaults(filters: LeadsAdvancedFilters): boolean {
  const activeKeys = Object.keys(filters).filter(k => {
    const v = filters[k as keyof LeadsAdvancedFilters];
    return v !== undefined && v !== null && v !== '';
  });
  const defaultKeys = Object.keys(DEFAULT_FILTERS);

  if (activeKeys.length !== defaultKeys.length) return false;

  return defaultKeys.every(key => {
    // `formAnswers` (D3) tem shape estruturado (Array<{fieldId, values}>), não
    // participa do comparativo scalar string|string[] deste helper — excluído
    // da leitura genérica para não quebrar o narrowing abaixo.
    const filterValue = filters[key as Exclude<keyof LeadsAdvancedFilters, 'formAnswers'>];
    const defaultValue = DEFAULT_FILTERS[key as Exclude<keyof LeadsAdvancedFilters, 'formAnswers'>];

    if (Array.isArray(filterValue) && Array.isArray(defaultValue)) {
      return (
        filterValue.length === defaultValue.length &&
        filterValue.every(v => (defaultValue as string[]).includes(v))
      );
    }
    return filterValue === defaultValue;
  });
}

// ═══════════════════════════════════════════════════════
// PROPS
// ═══════════════════════════════════════════════════════

/**
 * Props do LeadsAdvancedFilters
 */
export interface LeadsAdvancedFiltersProps {
  /** Filtros atuais */
  filters: LeadsAdvancedFilters;
  /** Callback quando filtros mudam */
  onFiltersChange: (filters: LeadsAdvancedFilters) => void;
  /**
   * Modo de renderização (fidelidade DS 3.3):
   * - 'full' (default): chips + "+ Filtro" + Limpar (compat LeadsListPage)
   * - 'toolbar': APENAS o botão `.btn` "Filtros" com ct-badge + popover (toolbar)
   * - 'chips': APENAS os fchips + "Limpar tudo" (filterbar abaixo do toolbar)
   */
  variant?: 'full' | 'toolbar' | 'chips';
  /** Forma pill do trigger 'toolbar' (`.m-chip` mobile: 34px, rounded-full). */
  pill?: boolean;
  /** Filtro por nível de alerta (kanban) — vira categoria "Nível de alerta" no popover + fchip. */
  alertLevelFilter?: AlertLevel;
  /** Callback do nível de alerta (undefined = Todos). Presença habilita a categoria. */
  onAlertLevelFilterChange?: (level: AlertLevel | undefined) => void;
  /**
   * `funnel.capture.formId` do funil selecionado — o formulário padrão da
   * seção "Formulário" (uma pergunta de escolha = um filtro) quando nenhum foi
   * escolhido em "Formulário respondido".
   */
  captureFormId?: string;
}

/** Opções do nível de alerta (kanban) — mesmas cores/labels do antigo segmented. */
const ALERT_LEVEL_OPTIONS: Array<{ label: string; level: AlertLevel | undefined; dot?: string }> = [
  { label: 'Todos', level: undefined },
  { label: 'Em dia', level: 'ok', dot: '#10B981' },
  { label: 'Alerta', level: 'warning', dot: '#F59E0B' },
  { label: 'Crítico', level: 'critical', dot: '#EF4444' },
  { label: 'Silenciados', level: 'snoozed', dot: '#94A3B8' },
];

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

/**
 * Filtros avançados com design chip-based (estilo Linear/Attio).
 *
 * Filtros ativos são exibidos como chips clicáveis. Novos filtros
 * são adicionados via popover com categorias e seletor de valores.
 *
 * @example
 * ```tsx
 * <LeadsAdvancedFiltersComponent
 *   filters={filters}
 *   onFiltersChange={setFilters}
 * />
 * ```
 */
export const LeadsAdvancedFiltersComponent: React.FC<LeadsAdvancedFiltersProps> = ({
  filters,
  onFiltersChange,
  variant = 'full',
  pill = false,
  alertLevelFilter,
  onAlertLevelFilterChange,
  captureFormId,
}) => {
  // ── Popover state ──
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'categories' | 'values'>('categories');
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [optionSearchQuery, setOptionSearchQuery] = useState('');

  // ── Local values for value selector ──
  const [localValues, setLocalValues] = useState<string[] | string>([]);
  const [localDateFrom, setLocalDateFrom] = useState('');
  const [localDateTo, setLocalDateTo] = useState('');

  // ── Positioning ──
  // HTMLElement: aponta pro botão (variants full/pill) ou pro wrapper do
  // FiltersButton shared (toolbar — o primitivo não expõe ref).
  const triggerRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  // ── Dynamic dropdown data for campaign/adset/ad/tags filters ──
  const { data: campaignNames } = useLeadsCampaignsDropdown();
  const { data: adsetNames } = useLeadsAdsetsDropdown();
  const { data: adNames } = useLeadsAdsDropdown();
  const { data: originNames } = useLeadsOriginsDropdown();
  const { data: tagNames } = useLeadsTagsDropdown();
  const { data: typeNames } = useLeadsTypesDropdown();

  // ── Funis + etapas dos funis JÁ selecionados (seção "Funil") ──
  const { data: funnels } = useFunnelsDropdown();
  const selectedFunnelIds = useMemo(() => {
    const value = filters.funnelId;
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [filters.funnelId]);
  const { stepsByFunnel } = useFunnelStepsByFunnels(selectedFunnelIds);

  // ── Seção "Formulário": o formulário escolhido no filtro, ou o de captura do funil ──
  const { data: formOptionsData } = useFormsDropdown('form');
  const effectiveFormId = filters.formAnswersFormId ?? captureFormId;
  const { data: sectionForm } = useFormFields(effectiveFormId, { enabled: !!effectiveFormId });
  const formDefinitions = useMemo(() => buildFormDefinitions(sectionForm?.fields), [sectionForm]);

  // ── Merged filter definitions (static + dynamic) ──
  const allFilterDefinitions = useMemo<Record<string, FilterDefinition>>(() => {
    const campaignOptions: FilterOption[] = (campaignNames || []).map(name => ({ value: name, label: name }));
    const adsetOptions: FilterOption[] = (adsetNames || []).map(name => ({ value: name, label: name }));
    const adOptions: FilterOption[] = (adNames || []).map(name => ({ value: name, label: name }));
    const originOptions: FilterOption[] = (originNames || []).map(name => ({ value: name, label: name }));
    const tagOptions: FilterOption[] = (tagNames || []).map(name => ({ value: name, label: name }));
    const typeOptions: FilterOption[] = (typeNames || []).map(name => ({ value: name, label: name }));

    const funnelOptions: FilterOption[] = (funnels || []).map(funnel => ({ value: funnel.id, label: funnel.name }));

    // Com MAIS DE UM funil escolhido o nome da etapa sozinho é ambíguo
    // ("Novo" existe em quase todo funil) — aí o rótulo ganha o funil na
    // frente. Com um só, prefixar seria ruído.
    const prefixWithFunnel = selectedFunnelIds.length > 1;
    const stepOptions: FilterOption[] = selectedFunnelIds.flatMap((funnelId) => {
      const funnelName = funnels?.find(funnel => funnel.id === funnelId)?.name ?? '';
      return (stepsByFunnel[funnelId] ?? []).map(step => ({
        value: step.id,
        label: prefixWithFunnel && funnelName ? `${funnelName} › ${step.name}` : step.name,
      }));
    });

    return {
      ...FILTER_DEFINITIONS,
      funnelId: {
        key: 'funnelId',
        label: 'Funil',
        type: 'multiselect',
        options: funnelOptions,
        placeholder: 'Buscar funil...',
      },
      stepId: {
        key: 'stepId',
        label: 'Etapa',
        type: 'multiselect',
        options: stepOptions,
        placeholder: 'Buscar etapa...',
        emptyHint: selectedFunnelIds.length === 0
          ? 'Escolha um funil primeiro — as etapas mudam de funil para funil.'
          : 'Este funil não tem etapas cadastradas.',
      },
      type: {
        key: 'type',
        label: 'Tipo',
        type: 'multiselect',
        options: typeOptions,
        placeholder: 'Buscar tipo...',
      },
      origin: {
        key: 'origin',
        label: 'Origem',
        type: 'multiselect',
        options: originOptions,
        placeholder: 'Buscar origem...',
      },
      campaignName: {
        key: 'campaignName',
        label: 'Campanha',
        type: 'multiselect',
        options: campaignOptions,
        placeholder: 'Buscar campanha...',
      },
      adsetName: {
        key: 'adsetName',
        label: 'Conjunto de Anúncios',
        type: 'multiselect',
        options: adsetOptions,
        placeholder: 'Buscar conjunto...',
      },
      adName: {
        key: 'adName',
        label: 'Anúncio',
        type: 'multiselect',
        options: adOptions,
        placeholder: 'Buscar anúncio...',
      },
      tags: {
        key: 'tags',
        label: 'Tags',
        type: 'multiselect',
        options: tagOptions,
        placeholder: 'Buscar tag...',
      },
      [FORM_ID_KEY]: {
        key: FORM_ID_KEY,
        label: 'Formulário respondido',
        type: 'multiselect',
        options: formOptionsData ?? [],
        placeholder: 'Buscar formulário...',
      },
      ...formDefinitions,
    };
  }, [campaignNames, adsetNames, adNames, originNames, tagNames, typeNames, formDefinitions, funnels, selectedFunnelIds, stepsByFunnel, formOptionsData]);

  // ── Categorias (estáticas + "Formulário": escolher o formulário e as perguntas dele) ──
  const categories = useMemo<FilterCategory[]>(() => {
    const formKeys = Object.keys(formDefinitions);
    const label = sectionForm ? `Formulário · ${sectionForm.name}` : 'Formulário';
    return [...FILTER_CATEGORIES, { id: 'form', label, icon: 'file-text', filters: [FORM_ID_KEY, ...formKeys] }];
  }, [formDefinitions, sectionForm]);

  // ── Derived state ──
  const activeFilters = useMemo(() => getActiveFilters(filters, allFilterDefinitions), [filters, allFilterDefinitions]);
  const hasFilters = activeFilters.length > 0;
  const isDefaults = isFiltersEqualToDefaults(filters);
  const currentFilterDef = selectedFilter ? allFilterDefinitions[selectedFilter] : null;

  // ── Update popover position ──
  // Âncora: trigger quando existe (variants full/toolbar); senão o container
  // dos chips (variant 'chips' — edição de chip abre o popover sob a filterbar).
  useEffect(() => {
    const anchor = triggerRef.current ?? containerRef.current;
    if (isOpen && anchor) {
      const rect = anchor.getBoundingClientRect();
      // Clamp na viewport (painel w-72 = 288px) — no mobile o trigger fica na
      // borda direita e o popover vazava pra fora da tela
      const PANEL_W = 288;
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - PANEL_W - 8)),
      });
    }
  }, [isOpen]);

  // ── Filtered categories for search ──
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;

    const query = searchQuery.toLowerCase();
    return categories
      .map(cat => ({
        ...cat,
        filters: cat.filters.filter(fKey => {
          const def = allFilterDefinitions[fKey];
          return def?.label.toLowerCase().includes(query);
        }),
      }))
      .filter(cat => cat.filters.length > 0);
  }, [searchQuery, allFilterDefinitions, categories]);

  // ── Filtered options for multiselect ──
  const filteredOptions = useMemo(() => {
    if (!currentFilterDef?.options) return [];
    if (!optionSearchQuery.trim()) return currentFilterDef.options;
    const query = optionSearchQuery.toLowerCase();
    return currentFilterDef.options.filter(o =>
      o.label.toLowerCase().includes(query)
    );
  }, [currentFilterDef, optionSearchQuery]);

  // ── Open popover: add new filter ──
  const handleOpenAdd = () => {
    setStep('categories');
    setSelectedFilter(null);
    setSearchQuery('');
    setOptionSearchQuery('');
    setLocalValues([]);
    setLocalDateFrom('');
    setLocalDateTo('');
    setIsOpen(true);
  };

  // ── Open popover: edit existing chip ──
  const handleEditChip = (filterKey: string) => {
    setStep('values');
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');

    const editRange = dateRangeKeys(allFilterDefinitions[filterKey]);
    if (editRange) {
      setLocalDateFrom(filters[editRange.fromKey] || '');
      setLocalDateTo(filters[editRange.toKey] || '');
    } else if (isFormKey(filterKey)) {
      setLocalValues([...getFormValues(filters, fieldIdFromKey(filterKey))]);
    } else {
      // `formAnswers` (D3) não é multiselect genérico — excluído do cast (shape próprio, editor dedicado).
      const value = filters[filterKey as Exclude<keyof LeadsAdvancedFilters, 'formAnswers'>];
      if (allFilterDefinitions[filterKey]?.type === 'multiselect') {
        setLocalValues(
          Array.isArray(value) ? [...value] : value ? [value as string] : []
        );
      } else {
        setLocalValues((value as string) || '');
      }
    }

    setIsOpen(true);
  };

  // ── Select filter from category menu ──
  const handleSelectCategory = (filterKey: string) => {
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');

    const selectedRange = dateRangeKeys(allFilterDefinitions[filterKey]);
    if (selectedRange) {
      setLocalDateFrom(filters[selectedRange.fromKey] || '');
      setLocalDateTo(filters[selectedRange.toKey] || '');
    } else if (isFormKey(filterKey)) {
      setLocalValues([...getFormValues(filters, fieldIdFromKey(filterKey))]);
    } else {
      // `formAnswers` (D3) não é multiselect genérico — excluído do cast (shape próprio, editor dedicado).
      const currentValue = filters[filterKey as Exclude<keyof LeadsAdvancedFilters, 'formAnswers'>];
      if (allFilterDefinitions[filterKey]?.type === 'multiselect') {
        setLocalValues(
          Array.isArray(currentValue)
            ? [...currentValue]
            : currentValue
              ? [currentValue as string]
              : []
        );
      } else {
        setLocalValues((currentValue as string) || '');
      }
    }

    setStep('values');
  };

  // ── Apply selected values ──
  const handleApply = () => {
    if (!selectedFilter) return;

    const newFilters = { ...filters };

    if (isFormKey(selectedFilter)) {
      onFiltersChange(setFormValues(filters, fieldIdFromKey(selectedFilter), Array.isArray(localValues) ? localValues : []));
      setIsOpen(false);
      return;
    }

    // Um formulário só. Trocar de formulário tira as perguntas do anterior.
    if (selectedFilter === FORM_ID_KEY) {
      const escolhido = Array.isArray(localValues) ? localValues[localValues.length - 1] : localValues;
      const { formAnswersFormId: anterior, formAnswers: _perguntas, ...rest } = filters;
      if (escolhido && escolhido === anterior) {
        setIsOpen(false);
        return;
      }
      onFiltersChange(escolhido ? { ...rest, formAnswersFormId: escolhido } : rest);
      setIsOpen(false);
      return;
    }

    const applyRange = dateRangeKeys(allFilterDefinitions[selectedFilter]);
    if (applyRange) {
      if (localDateFrom) newFilters[applyRange.fromKey] = localDateFrom;
      else delete newFilters[applyRange.fromKey];
      if (localDateTo) newFilters[applyRange.toKey] = localDateTo;
      else delete newFilters[applyRange.toKey];
    } else {
      const isEmpty =
        localValues === undefined ||
        localValues === null ||
        localValues === '' ||
        (Array.isArray(localValues) && localValues.length === 0);

      if (isEmpty) {
        delete newFilters[selectedFilter as keyof LeadsAdvancedFilters];
      } else {
        (newFilters as Record<string, unknown>)[selectedFilter] = localValues;
      }
    }

    if (selectedFilter === 'funnelId') {
      const nextFunnelIds = Array.isArray(localValues) ? localValues : localValues ? [localValues] : [];
      onFiltersChange(pruneStepsOutsideFunnels(newFilters, nextFunnelIds, stepsByFunnel));
      setIsOpen(false);
      return;
    }

    onFiltersChange(newFilters);
    setIsOpen(false);
  };

  // ── Remove a chip ──
  const handleRemoveChip = (filterKey: string) => {
    const newFilters = { ...filters };

    if (isFormKey(filterKey)) {
      onFiltersChange(setFormValues(filters, fieldIdFromKey(filterKey), []));
      return;
    }

    const removeRange = dateRangeKeys(allFilterDefinitions[filterKey]);
    if (removeRange) {
      delete newFilters[removeRange.fromKey];
      delete newFilters[removeRange.toKey];
    } else {
      delete newFilters[filterKey as keyof LeadsAdvancedFilters];
    }

    // Tirar o formulário escolhido tira as perguntas dele junto.
    if (filterKey === FORM_ID_KEY) {
      delete newFilters.formAnswers;
    }

    // Tirar o funil tira as etapas dele junto — etapa órfã zeraria a listagem
    // (o backend exige funil E etapa ao mesmo tempo).
    if (filterKey === 'funnelId') {
      delete newFilters.stepId;
    }

    onFiltersChange(newFilters);
  };

  // ── Clear / Reset ──
  const handleClearOrReset = () => {
    if (isDefaults) {
      onFiltersChange({});
    } else {
      onFiltersChange(DEFAULT_FILTERS);
    }
  };

  // ── Toggle multiselect value ──
  const handleToggleOption = (value: string) => {
    // "Formulário respondido" é de escolha única: marcar outro troca.
    if (selectedFilter === FORM_ID_KEY) {
      setLocalValues(Array.isArray(localValues) && localValues[0] === value ? [] : [value]);
      return;
    }
    if (!Array.isArray(localValues)) {
      setLocalValues([value]);
      return;
    }
    if (localValues.includes(value)) {
      setLocalValues(localValues.filter(v => v !== value));
    } else {
      setLocalValues([...localValues, value]);
    }
  };

  // ── Close popover ──
  const handleClose = () => {
    setIsOpen(false);
  };

  // ── Back to categories ──
  const handleBack = () => {
    setStep('categories');
    setSelectedFilter(null);
    setSearchQuery('');
  };

  // ── Check if a filter is active ──
  const isFilterActive = (filterKey: string): boolean => {
    if (isFormKey(filterKey)) return getFormValues(filters, fieldIdFromKey(filterKey)).length > 0;
    const activeRange = dateRangeKeys(allFilterDefinitions[filterKey]);
    if (activeRange) {
      return !!(filters[activeRange.fromKey] || filters[activeRange.toKey]);
    }
    const value = filters[filterKey as keyof LeadsAdvancedFilters];
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  // Contagem p/ o ct-badge do botão "Filtros" (toolbar) — inclui o nível de alerta
  const badgeCount = activeFilters.length + (alertLevelFilter !== undefined ? 1 : 0);
  const alertOption = ALERT_LEVEL_OPTIONS.find((o) => o.level === alertLevelFilter);

  return (
    <div ref={containerRef} className="flex items-center gap-2 flex-wrap">
      {/* ═══ Chips ativos (fchips) — escondidos no variant 'toolbar' ═══ */}
      {variant !== 'toolbar' && activeFilters.map((filter) => (
        <span
          key={filter.key}
          className="inline-flex"
          data-testid={isFormKey(filter.key) ? `leads-filter-chip-form-${fieldIdFromKey(filter.key)}` : `leads-filter-chip-${filter.key}`}
        >
          <FilterChip
            label={filter.label}
            value={filter.displayValue}
            onClick={() => handleEditChip(filter.key)}
            onRemove={() => handleRemoveChip(filter.key)}
          />
        </span>
      ))}

      {/* Chip do nível de alerta (kanban) */}
      {variant !== 'toolbar' && onAlertLevelFilterChange && alertLevelFilter !== undefined && alertOption && (
        <FilterChip
          label="Alerta"
          value={alertOption.label}
          onClick={() => {
            setSelectedFilter('__alertLevel');
            setStep('values');
            setIsOpen(true);
          }}
          onRemove={() => onAlertLevelFilterChange(undefined)}
        />
      )}

      {/* ═══ Trigger ═══ */}
      {variant === 'toolbar' ? (
        pill ? (
          /* `.m-chip` mobile do mockup: 34px, rounded-full (funil + badge roxo inline — padrão F05) */
          <button
            ref={(el) => { triggerRef.current = el; }}
            type="button"
            onClick={handleOpenAdd}
            className="inline-flex items-center gap-[7px] text-[13px] font-medium whitespace-nowrap transition-colors flex-none h-[34px] px-3 rounded-full"
            style={{
              border: '1px solid var(--theme-input-border)',
              backgroundColor: 'var(--theme-card-background)',
              color: 'var(--theme-card-text)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
            data-testid="leads-filters-button"
          >
            <Icon name="filter" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
            <span>Filtros</span>
            {badgeCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white ml-0.5"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                {badgeCount}
              </span>
            )}
          </button>
        ) : (
          /* Botão "Filtros" canônico do DS (F05): FiltersButton shared — funil + badge roxo inline */
          <span ref={(el) => { triggerRef.current = el; }} className="inline-flex flex-none">
            <FiltersButton
              onClick={handleOpenAdd}
              count={badgeCount}
              active={isOpen}
              data-testid="leads-filters-button"
            />
          </span>
        )
      ) : variant === 'full' ? (
        <button
          ref={(el) => { triggerRef.current = el; }}
          type="button"
          onClick={handleOpenAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
          style={{
            backgroundColor: 'var(--theme-button-ghost)',
            color: 'var(--theme-header-text)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--theme-header-button-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost)';
          }}
        >
          <Icon name="plus" className="w-3.5 h-3.5" />
          <span>Filtro</span>
        </button>
      ) : null}

      {/* ═══ Limpar tudo (.fclear) ═══ */}
      {variant === 'chips' && (hasFilters || alertLevelFilter !== undefined) && (
        <button
          type="button"
          onClick={() => {
            handleClearOrReset();
            onAlertLevelFilterChange?.(undefined);
          }}
          className="text-xs font-semibold flex-none transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
        >
          Limpar tudo
        </button>
      )}
      {variant === 'full' && hasFilters && (
        <button
          type="button"
          onClick={handleClearOrReset}
          className="text-xs font-medium px-2 py-1.5 rounded transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--theme-header-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          {isDefaults ? 'Limpar' : 'Resetar'}
        </button>
      )}

      {/* ═══ Popover ═══ */}
      {isOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[100]"
              onClick={handleClose}
            />

            {/* Panel */}
            <div
              className="fixed z-[101] w-72 rounded-lg shadow-xl flex flex-col"
              style={{
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                maxHeight: '420px',
              }}
            >
              {selectedFilter === '__alertLevel' && step === 'values' ? (
                /* ═══ Painel do Nível de alerta (kanban) — single-select ═══ */
                <>
                  <div className="flex items-center gap-2 p-3 flex-shrink-0" style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <button
                      type="button"
                      onClick={handleBack}
                      className="p-1 rounded transition-colors"
                      style={{ color: '#64748b' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      aria-label="Voltar"
                    >
                      <Icon name="chevron-left" className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-semibold" style={{ color: '#1e293b' }}>Nível de alerta</span>
                  </div>
                  <div className="py-1 overflow-y-auto">
                    {ALERT_LEVEL_OPTIONS.map((opt) => {
                      const activeOpt = alertLevelFilter === opt.level;
                      return (
                        <button
                          key={opt.label}
                          type="button"
                          onClick={() => {
                            onAlertLevelFilterChange?.(opt.level);
                            handleClose();
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm transition-colors text-left"
                          style={{ color: '#1e293b', fontWeight: activeOpt ? 600 : 400 }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          data-testid={`leads-alert-level-${opt.label}`}
                        >
                          <span className="flex items-center gap-2">
                            {opt.dot && (
                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: opt.dot }} />
                            )}
                            {opt.label}
                          </span>
                          {activeOpt && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : step === 'categories' ? (
                /* ═══════════════════════════════════════ */
                /* STEP 1: CATEGORY LIST                  */
                /* ═══════════════════════════════════════ */
                <>
                  {/* Search */}
                  <div
                    className="p-3 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <div className="relative">
                      <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                        style={{ color: '#9ca3af' }} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar filtro..."
                        className="w-full h-[38px] pl-8 pr-3 py-1.5 text-sm rounded-md border"
                        style={{
                          backgroundColor: '#f9fafb',
                          borderColor: '#e5e7eb',
                          color: '#1e293b',
                        }}
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Category Groups */}
                  <div className="overflow-y-auto flex-1 py-1">
                    {/* Nível de alerta (kanban) — categoria sintética no topo */}
                    {onAlertLevelFilterChange && !searchQuery.trim() && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFilter('__alertLevel');
                          setStep('values');
                        }}
                        className="w-full flex items-center justify-between py-2 text-sm transition-colors text-left"
                        style={{ paddingLeft: '0.75rem', paddingRight: '0.75rem', color: '#1e293b', fontWeight: 500 }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid="leads-filter-alert-level"
                      >
                        <span className="flex items-center gap-2">
                          <Icon name="bell" className="w-3.5 h-3.5" style={{ color: '#9ca3af' }} />
                          Nível de alerta
                        </span>
                        {alertLevelFilter !== undefined && (
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#8b5cf6' }} />
                        )}
                      </button>
                    )}
                    {filteredCategories.length === 0 ? (
                      <div
                        className="text-center py-6 text-sm"
                        style={{ color: '#9ca3af' }}
                      >
                        Nenhum filtro encontrado
                      </div>
                    ) : (
                      filteredCategories.map((category, catIndex) => (
                        <div key={category.id}>
                          {/* Category Header */}
                          <div
                            className="flex items-center gap-2 px-3 py-1.5"
                            style={{
                              color: '#9ca3af',
                              ...(catIndex > 0
                                ? {
                                    borderTop: '1px solid #f3f4f6',
                                    marginTop: '4px',
                                    paddingTop: '8px',
                                  }
                                : {}),
                            }}
                          >
                            <Icon name={category.icon} className="w-3.5 h-3.5" />
                            <span className="text-[11px] font-semibold uppercase tracking-wider">
                              {category.label}
                            </span>
                          </div>

                          {/* Filter Items */}
                          {category.filters.map((filterKey) => {
                            const def = allFilterDefinitions[filterKey];
                            if (!def) return null;

                            const active = isFilterActive(filterKey);

                            return (
                              <button
                                key={filterKey}
                                type="button"
                                onClick={() => handleSelectCategory(filterKey)}
                                className="w-full flex items-center justify-between py-2 text-sm transition-colors text-left"
                                style={{
                                  paddingLeft: '2rem',
                                  paddingRight: '0.75rem',
                                  color: '#1e293b',
                                }}
                                data-testid={isFormKey(filterKey) ? `leads-filter-form-${fieldIdFromKey(filterKey)}` : `leads-filter-${filterKey}`}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <span>{def.label}</span>
                                {active && (
                                  <div
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: '#8b5cf6' }}
                                  />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                /* ═══════════════════════════════════════ */
                /* STEP 2: VALUE SELECTOR                 */
                /* ═══════════════════════════════════════ */
                <>
                  {/* Header with back button */}
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <button
                      type="button"
                      onClick={handleBack}
                      className="p-1 rounded transition-colors"
                      style={{ color: '#64748b' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon name="chevron-left" className="w-4 h-4" />
                    </button>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: '#1e293b' }}
                    >
                      {currentFilterDef?.label}
                    </span>
                  </div>

                  {/* Value Content */}
                  <div className="overflow-y-auto flex-1">
                    {/* ── MultiSelect ── */}
                    {currentFilterDef?.type === 'multiselect' && (
                      <>
                        {/* Search (only if many options) */}
                        {(currentFilterDef.options?.length || 0) > 5 && (
                          <div className="px-3 pt-3 pb-1">
                            <div className="relative">
                              <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                                style={{ color: '#9ca3af' }} />
                              <input
                                type="text"
                                value={optionSearchQuery}
                                onChange={(e) =>
                                  setOptionSearchQuery(e.target.value)
                                }
                                placeholder={currentFilterDef.placeholder || 'Buscar...'}
                                className="w-full h-[38px] pl-8 pr-3 py-1.5 text-sm rounded-md border"
                                style={{
                                  backgroundColor: '#f9fafb',
                                  borderColor: '#e5e7eb',
                                  color: '#1e293b',
                                }}
                                autoFocus
                              />
                            </div>
                          </div>
                        )}

                        {/* Options */}
                        <div className="p-2 space-y-0.5">
                          {filteredOptions.length === 0 ? (
                            <div
                              className="text-center py-4 px-3 text-sm"
                              style={{ color: '#9ca3af' }}
                            >
                              {(currentFilterDef.options?.length ?? 0) === 0 && currentFilterDef.emptyHint
                                ? currentFilterDef.emptyHint
                                : 'Nenhuma opção encontrada'}
                            </div>
                          ) : (
                            filteredOptions.map((option) => {
                              const isSelected =
                                Array.isArray(localValues) &&
                                localValues.includes(option.value);

                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleToggleOption(option.value)}
                                  className="w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left"
                                  style={{
                                    backgroundColor: isSelected
                                      ? '#f5f3ff'
                                      : 'transparent',
                                    color: '#1e293b',
                                  }}
                                  data-testid={selectedFilter && isFormKey(selectedFilter) ? `leads-filter-form-option-${fieldIdFromKey(selectedFilter)}-${option.value}` : `leads-filter-option-${selectedFilter ?? ''}-${option.value}`}
                                  onMouseEnter={(e) => {
                                    if (!isSelected)
                                      e.currentTarget.style.backgroundColor =
                                        '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      isSelected ? '#f5f3ff' : 'transparent';
                                  }}
                                >
                                  <span>{option.label}</span>
                                  {isSelected && (
                                    <div
                                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{ backgroundColor: '#8b5cf6' }}
                                    >
                                      <Icon name="check" className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>

                        {/* Selection counter */}
                        {Array.isArray(localValues) && localValues.length > 0 && (
                          <div
                            className="px-3 py-1.5 text-[11px]"
                            style={{ color: '#9ca3af' }}
                          >
                            {localValues.length} selecionado
                            {localValues.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </>
                    )}

                    {/* ── Text Input ── */}
                    {currentFilterDef?.type === 'text' && (
                      <div className="p-3">
                        <input
                          type="text"
                          value={typeof localValues === 'string' ? localValues : ''}
                          onChange={(e) => setLocalValues(e.target.value)}
                          placeholder={currentFilterDef.placeholder}
                          className="w-full h-[38px] px-3 py-2 text-sm rounded-md border"
                          style={{
                            backgroundColor: '#ffffff',
                            borderColor: '#d1d5db',
                            color: '#1e293b',
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#8b5cf6';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#d1d5db';
                          }}
                          autoFocus
                        />
                      </div>
                    )}

                    {/* ── Date Range ── */}
                    {currentFilterDef?.type === 'daterange' && (
                      <div className="p-3 space-y-3">
                        <div>
                          <label
                            className="block text-xs font-medium mb-1"
                            style={{ color: '#64748b' }}
                          >
                            De
                          </label>
                          <input
                            type="date"
                            value={localDateFrom}
                            onChange={(e) => setLocalDateFrom(e.target.value)}
                            className="w-full h-[38px] px-3 py-2 text-sm rounded-md border"
                            style={{
                              backgroundColor: '#ffffff',
                              borderColor: '#d1d5db',
                              color: '#1e293b',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#8b5cf6';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                          />
                        </div>
                        <div>
                          <label
                            className="block text-xs font-medium mb-1"
                            style={{ color: '#64748b' }}
                          >
                            Até
                          </label>
                          <input
                            type="date"
                            value={localDateTo}
                            onChange={(e) => setLocalDateTo(e.target.value)}
                            className="w-full h-[38px] px-3 py-2 text-sm rounded-md border"
                            style={{
                              backgroundColor: '#ffffff',
                              borderColor: '#d1d5db',
                              color: '#1e293b',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#8b5cf6';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Apply Footer */}
                  <div
                    className="px-3 py-2.5 flex items-center justify-end flex-shrink-0"
                    style={{ borderTop: '1px solid #e5e7eb' }}
                  >
                    <button
                      type="button"
                      onClick={handleApply}
                      className="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
                      style={{
                        backgroundColor: '#8b5cf6',
                        color: '#ffffff',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#7c3aed';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#8b5cf6';
                      }}
                      data-testid="leads-filters-apply"
                    >
                      Aplicar
                    </button>
                  </div>
                </>
              )}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

LeadsAdvancedFiltersComponent.displayName = 'LeadsAdvancedFiltersComponent';
